import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@neo/primitives/alert-dialog'
import { Button } from '@neo/primitives/button'
import { SquarePen } from 'lucide-react'
import { useState } from 'react'
import { useVibeAgentStore } from '../store'
import { newChatConfirmBody } from './new-chat-copy'
import { useVibeAgentApi } from './use-vibe-agent-api'

/**
 * Header icon that starts a fresh Vibe session, behind a confirm dialog: the
 * new chat has no access to the current conversation's context, and starting
 * one stops a build in progress and discards staged messages — the dialog
 * says exactly as much. Hidden until a conversation exists (the empty state
 * needs no "new chat"). Replaces the dropdown-buried "Reset chat".
 */
export const VibeNewChatButton = () => {
  const hasTranscript = useVibeAgentStore((state) => state.transcript.length > 0)
  // The exact status set reset() cancels before wiping.
  const isBuildInProgress = useVibeAgentStore(
    (state) =>
      state.runStatus === 'running' ||
      state.runStatus === 'sending' ||
      state.runStatus === 'waiting_input',
  )
  const hasQueued = useVibeAgentStore((state) => state.queuedMessages.length > 0)
  const { reset } = useVibeAgentApi()
  const [open, setOpen] = useState(false)

  if (!hasTranscript) return null

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        tooltipContent="New chat"
        aria-label="New chat"
        data-testid="button-new-chat"
        className="text-secondary hover:text-foreground hover:bg-bg2 h-8 w-8 transition-colors duration-150"
      >
        <SquarePen className="w-4 h-4" />
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="dialog-new-chat">
          <AlertDialogHeader>
            <AlertDialogTitle>Start a new chat?</AlertDialogTitle>
            <AlertDialogDescription>
              {newChatConfirmBody(isBuildInProgress, hasQueued)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="button-new-chat-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                // Close before the (fire-and-forget) reset: the wipe unmounts
                // this button, and reset() never throws — the local wipe is
                // the user-visible outcome; sessionId turns null so the next
                // send opens a fresh backend session regardless.
                setOpen(false)
                void reset()
              }}
              data-testid="button-new-chat-confirm"
            >
              Start new chat
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
